/**
 * Note: A detailed workout of error handling can found in the file
 * "/src/error-handling.md".
 */
import React from 'react';

import { Category } from '../../api/api';
import { CommonModal } from '../commons/components/common-modal';
import { useTranslations } from '../commons/utils/translations/translate';

interface CategoryLostModalProps {
  category: Category;
  onClose: () => void;
  onRepeat: () => void;
}

interface CategoryLostErrorModalProps {
  onClose: () => void;
  onRepeat: () => void;
}

const EmptyLocationsModal: React.FC<CategoryLostErrorModalProps> = ({ onClose, onRepeat }) => {
  const { tICU } = useTranslations();
  return (
    <CommonModal
      title={tICU('widget.EMPTY_LOCATIONS_TITLE')}
      body={tICU('widget.EMPTY_LOCATIONS_MESSAGE')}
      primaryButtonText={tICU('widget.EMPTY_LOCATIONS_ADDRESS_BUTTON')}
      secondaryButtonText={tICU('widget.GENERAL_ERROR_MESSAGE.CONFIRMATION_BTN')}
      onPrimaryButtonClick={onRepeat}
      onSecondaryButtonClick={onClose}
      onClose={onClose}
    />
  );
};

const SelectedChoiceLostForAddressModal: React.FC<CategoryLostErrorModalProps> = ({
  onClose,
  onRepeat,
}) => {
  const { tICU } = useTranslations();
  return (
    <CommonModal
      title={tICU('widget.error_modal.SELECTED_CHOICE_LOST_FOR_ADDRESS.TITLE')}
      body={tICU('widget.error_modal.SELECTED_CHOICE_LOST_FOR_ADDRESS.DESCRIPTION')}
      primaryButtonText={tICU('widget.EMPTY_LOCATIONS_ADDRESS_BUTTON')}
      secondaryButtonText={tICU('widget.GENERAL_ERROR_MESSAGE.CONFIRMATION_BTN')}
      onPrimaryButtonClick={onRepeat}
      onSecondaryButtonClick={onClose}
      onClose={onClose}
    />
  );
};

export const CategoryLostModal: React.FC<CategoryLostModalProps> = ({ category, ...props }) => {
  /**
   * here session state refers to old session state because the new state from
   * mutation was not yet synced.
   */
  if (category.delivery_type === 'pickup' || category.delivery_type === 'instore') {
    return <EmptyLocationsModal {...props} />;
  }

  /**
   * Update from category-manager, address was required as a part of
   * selecting an option -> SELECTED_CHOICE_LOST_FOR_ADDRESS.
   */
  return <SelectedChoiceLostForAddressModal {...props} />;
};

interface ChoiceUnavailableForAddressModalProps {
  onClose: () => void;
  onRepeat: () => void;
}

export const ChoiceUnavailableForAddressModal: React.FC<ChoiceUnavailableForAddressModalProps> = ({
  onClose,
  onRepeat,
}) => {
  const { tICU } = useTranslations();
  return (
    <CommonModal
      title={tICU('widget.error_modal.CHOICE_UNAVAILABLE_FOR_ADDRESS.TITLE')}
      body={tICU('widget.error_modal.CHOICE_UNAVAILABLE_FOR_ADDRESS.DESCRIPTION')}
      primaryButtonText={tICU('widget.EMPTY_LOCATIONS_ADDRESS_BUTTON')}
      secondaryButtonText={tICU('widget.GENERAL_ERROR_MESSAGE.CONFIRMATION_BTN')}
      onPrimaryButtonClick={onRepeat}
      onSecondaryButtonClick={onClose}
      onClose={onClose}
    />
  );
};

interface SelectedChoiceLostModalProps {
  onClose: () => void;
}

export const SelectedChoiceLostModal: React.FC<SelectedChoiceLostModalProps> = ({ onClose }) => {
  const { tICU } = useTranslations();
  return (
    <CommonModal
      title={tICU('widget.error_modal.SELECTED_CHOICE_LOST.TITLE')}
      body={tICU('widget.error_modal.SELECTED_CHOICE_LOST.DESCRIPTION')}
      primaryButtonText={tICU('widget.GENERAL_ERROR_MESSAGE.CONFIRMATION_BTN')}
      onPrimaryButtonClick={onClose}
      onClose={onClose}
    />
  );
};

export const LocationLostModal: React.FC<SelectedChoiceLostModalProps> = ({ onClose }) => {
  const { tICU } = useTranslations();
  return (
    <CommonModal
      title={tICU('widget.error_modal.LOCATION_LOST.TITLE')}
      body={tICU('widget.error_modal.LOCATION_LOST.DESCRIPTION')}
      primaryButtonText={tICU('widget.GENERAL_ERROR_MESSAGE.CONFIRMATION_BTN')}
      onPrimaryButtonClick={onClose}
      onClose={onClose}
    />
  );
};
